import { getLogger } from "@ebay-business-components/marko-rum";

/**
 * Custom logger wrapper for RUM logging functionality
 */
export class AppLogger {
  private logger: any = null;

  constructor() {
    // Get the logger instance initialized by rum-inliner
    this.logger = getLogger();
  }

  /**
   * Log a business event
   */
  logEvent(
    type: string,
    kind: string,
    detail: string,
    additionalData?: Record<string, any>,
  ) {
    if (!this.logger) {
      console.warn("RUM logger not initialized");
      return;
    }

    this.logger.event({
      type: "event",
      kind,
      detail,
      ...additionalData,
    });
  }

  /**
   * Log user interactions (clicks, form submissions, etc.)
   */
  logUserInteraction(
    action: string,
    element: string,
    details?: Record<string, any>,
  ) {
    this.logEvent("event", "USER_INTERACTION", `${action}_${element}`, details);
  }

  /**
   * Log page view events
   */
  logPageView(page: string, additionalData?: Record<string, any>) {
    this.logEvent("event", "PAGE_VIEW", page, additionalData);
  }

  /**
   * Log navigation events
   */
  logNavigation(from: string, to: string, method: string = "click") {
    this.logEvent("event", "NAVIGATION", `${from}_TO_${to}`, { method });
  }

  /**
   * Log search events
   */
  logSearch(
    query: string,
    resultCount?: number,
    filters?: Record<string, any>,
  ) {
    this.logEvent("event", "SEARCH", "SEARCH_PERFORMED", {
      query,
      resultCount,
      filters,
    });
  }

  /**
   * Log item/content interactions
   */
  logItemInteraction(
    action: string,
    itemId: string,
    itemType: string = "item",
  ) {
    this.logEvent(
      "event",
      "ITEM_INTERACTION",
      `${action}_${itemType.toUpperCase()}`,
      {
        itemId,
        itemType,
      },
    );
  }

  /**
   * Log form interactions
   */
  logFormInteraction(formName: string, action: string, fieldName?: string) {
    this.logEvent("event", "FORM_INTERACTION", `${formName}_${action}`, {
      fieldName,
    });
  }

  /**
   * Log performance metrics
   */
  logPerformance(metric: string, value: number, unit: string = "ms") {
    this.logEvent("event", "PERFORMANCE", metric, { value, unit });
  }

  /**
   * Log errors
   */
  logError(error: Error, context?: Record<string, any>) {
    if (!this.logger) {
      console.error(
        "RUM logger not initialized, falling back to console:",
        error,
      );
      return;
    }

    this.logger.error(error, context);
  }

  /**
   * Log info messages
   */
  logInfo(message: string, data?: Record<string, any>) {
    if (!this.logger) {
      console.info(message, data);
      return;
    }

    // Use event method since RUM logger doesn't have info method
    this.logger.event({
      type: "event",
      kind: "INFO",
      detail: message,
      ...data,
    });
  }

  /**
   * Check if logger is available
   */
  isAvailable(): boolean {
    return this.logger !== null;
  }

  /**
   * Get the raw logger instance for advanced usage
   */
  getRawLogger() {
    return this.logger;
  }
}

// Create a singleton instance
export const appLogger = new AppLogger();

// Export convenience functions
export const logEvent = (
  type: string,
  kind: string,
  detail: string,
  additionalData?: Record<string, any>,
) => appLogger.logEvent(type, kind, detail, additionalData);

export const logUserInteraction = (
  action: string,
  element: string,
  details?: Record<string, any>,
) => appLogger.logUserInteraction(action, element, details);

export const logPageView = (
  page: string,
  additionalData?: Record<string, any>,
) => appLogger.logPageView(page, additionalData);

export const logNavigation = (from: string, to: string, method?: string) =>
  appLogger.logNavigation(from, to, method);

export const logSearch = (
  query: string,
  resultCount?: number,
  filters?: Record<string, any>,
) => appLogger.logSearch(query, resultCount, filters);

export const logItemInteraction = (
  action: string,
  itemId: string,
  itemType?: string,
) => appLogger.logItemInteraction(action, itemId, itemType);

export const logFormInteraction = (
  formName: string,
  action: string,
  fieldName?: string,
) => appLogger.logFormInteraction(formName, action, fieldName);

export const logPerformance = (metric: string, value: number, unit?: string) =>
  appLogger.logPerformance(metric, value, unit);

export const logError = (error: Error, context?: Record<string, any>) =>
  appLogger.logError(error, context);

export const logInfo = (message: string, data?: Record<string, any>) =>
  appLogger.logInfo(message, data);
